import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Clock, ChevronLeft, ChevronRight, Loader2, CheckCircle2, XCircle, Award, Download } from "lucide-react";

export default function Exam() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [exam, setExam] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    api.post(`/exams/${slug}/start`).then((r) => {
      setExam(r.data);
      setTimeLeft(r.data.duration_min * 60);
    }).catch((e) => {
      toast.error(apiErr(e.response?.data?.detail));
      nav("/account");
    }).finally(() => setStarting(false));
  }, [slug, nav]);

  const submit = useCallback(async (auto = false) => {
    if (submitting || result) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/exams/submit", { attempt_id: exam.attempt_id, answers });
      setResult(data);
      if (auto) toast.info("Time's up — your exam was submitted.");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  }, [submitting, result, exam, answers]);

  useEffect(() => {
    if (!exam || result) return;
    if (timeLeft <= 0) { submit(true); return; }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, exam, result, submit]);

  if (starting) return <div className="min-h-screen bg-app flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" />Preparing the exam…</div>;
  if (!exam) return null;

  if (result) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center px-4 py-12" data-testid="exam-result">
        <div className="w-full max-w-lg glass rounded-2xl p-8 text-center">
          {result.passed ? <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto" /> : <XCircle className="h-16 w-16 text-rose-400 mx-auto" />}
          <h1 className="font-display text-3xl font-extrabold text-white mt-4">{result.passed ? "Congratulations! You passed" : "You did not pass"}</h1>
          <div className="text-6xl font-display font-extrabold gold-gradient-text my-6">{result.score}%</div>
          <p className="text-slate-300">Correct answers: {result.correct} / {result.total} • Pass mark: {result.passing_score}%</p>
          {result.passed && result.certificate_number && (
            <div className="mt-6 bg-gold/10 border border-gold/30 rounded-xl p-5">
              <Award className="h-6 w-6 text-gold mx-auto mb-2" />
              <p className="text-sm text-slate-300">Your certificate has been issued:</p>
              <p className="font-mono text-gold font-semibold mt-1">{result.certificate_number}</p>
              <a href={`${API}/certificates/${encodeURIComponent(result.certificate_number)}/download`} target="_blank" rel="noreferrer">
                <Button className="btn-gold mt-4" data-testid="exam-download-cert"><Download className="h-4 w-4 mr-2" />Download certificate</Button>
              </a>
            </div>
          )}
          <Button variant="outline" className="mt-6 border-white/20 text-white hover:bg-white/10 bg-transparent" onClick={() => nav("/account")} data-testid="exam-back-account">
            Back to my account
          </Button>
        </div>
      </div>
    );
  }

  const q = exam.questions[idx];
  const mm = String(Math.floor(timeLeft / 60)).padStart(2, "0");
  const ss = String(timeLeft % 60).padStart(2, "0");
  const answered = Object.keys(answers).length;

  return (
    <div className="min-h-screen bg-app" data-testid="exam-page">
      <header className="glass border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Logo className="h-8" showText />
          <div className="flex items-center gap-6">
            <span className="text-sm text-slate-300">{answered}/{exam.questions.length} answered</span>
            <span className={`flex items-center gap-2 font-mono font-semibold px-3 py-1.5 rounded-lg ${timeLeft < 120 ? "bg-rose-500/20 text-rose-300" : "bg-surface-2 text-gold"}`} data-testid="exam-timer">
              <Clock className="h-4 w-4" />{mm}:{ss}
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <p className="text-xs font-mono uppercase tracking-wider text-gold mb-2">{exam.course_title}</p>
          <h2 className="font-display text-lg font-semibold text-slate-300">Question {idx + 1} of {exam.questions.length}</h2>
          <div className="bg-surface-card rounded-xl p-6 border border-white/10 mt-4" style={{ backgroundColor: "#131C38" }}>
            <p className="text-white whitespace-pre-wrap leading-relaxed" data-testid="exam-question-stem">{q.stem}</p>
            <div className="mt-6 space-y-3">
              {Object.entries(q.options).map(([k, v]) => {
                const sel = answers[String(q.id)] === k;
                return (
                  <button key={k} onClick={() => setAnswers({ ...answers, [String(q.id)]: k })} data-testid={`exam-option-${k}`}
                    className={`w-full text-left flex items-start gap-3 p-4 rounded-lg border transition-colors ${sel ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/30 bg-surface-2"}`}>
                    <span className={`h-6 w-6 shrink-0 rounded-full flex items-center justify-center text-xs font-mono font-bold ${sel ? "bg-gold text-[#0B132B]" : "bg-white/10 text-slate-300"}`}>{k}</span>
                    <span className="text-sm text-slate-200">{v}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex justify-between mt-6">
            <Button variant="outline" disabled={idx === 0} onClick={() => setIdx(idx - 1)} className="border-white/20 text-white hover:bg-white/10 bg-transparent" data-testid="exam-prev">
              <ChevronLeft className="h-4 w-4 mr-1" />Previous
            </Button>
            {idx < exam.questions.length - 1 ? (
              <Button className="btn-gold" onClick={() => setIdx(idx + 1)} data-testid="exam-next">Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
            ) : (
              <Button className="btn-gold" onClick={() => submit(false)} disabled={submitting} data-testid="exam-submit">
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Submit exam
              </Button>
            )}
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="bg-surface rounded-xl p-5 border border-white/10 sticky top-24">
            <h3 className="font-display font-semibold text-white mb-3 text-sm">Question navigator</h3>
            <div className="grid grid-cols-8 lg:grid-cols-6 gap-2">
              {exam.questions.map((qq, i) => (
                <button key={qq.id} onClick={() => setIdx(i)} data-testid={`exam-nav-${i}`}
                  className={`h-8 w-8 rounded text-xs font-mono font-semibold transition-colors ${i === idx ? "bg-electric text-white" : answers[String(qq.id)] ? "bg-gold/80 text-[#0B132B]" : "bg-surface-2 text-slate-400 hover:bg-white/10"}`}>
                  {i + 1}
                </button>
              ))}
            </div>
            <Button className="w-full btn-gold mt-5" onClick={() => submit(false)} disabled={submitting} data-testid="exam-submit-sidebar">
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Submit exam
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
}
